<template>
    <guest-layout background-class="bg-white" :color-scheme="'light'">
        <!-- Main Content -->
        <main>
            <!-- Hero Section -->
            <div class="pb-8 sm:pb-12 lg:pb-12">
                <div class="pt-8 overflow-hidden sm:pt-12 lg:relative lg:py-48">
                    <div class="mx-auto max-w-md px-4 sm:max-w-3xl sm:px-6 lg:px-8 lg:max-w-7xl lg:grid lg:grid-cols-2 lg:gap-24">
                        <div>
                            <div v-motion-slide-top class="mt-20">
                                <div>
                                    <a target="_blank" href="https://help.livck.com/changelog" class="inline-flex space-x-4">
                                        <span class="rounded bg-primary-50 px-2.5 py-1 text-xs font-semibold text-primary-600 tracking-wide uppercase"> {{ __('What\'s new') }} </span>
                                        <span class="inline-flex items-center text-sm font-medium text-primary-600 space-x-1">
                                          <span>{{ __('Visit the changelog') }}</span>
                                          <ChevronRightIcon class="h-5 w-5" aria-hidden="true" />
                                        </span>
                                    </a>
                                </div>
                                <div class="mt-6 sm:max-w-xl">
                                    <h1 class="text-4xl font-extrabold text-gray-900 tracking-tight sm:text-5xl">{{ __('Status management for growing teams') }}</h1>
                                    <p class="mt-6 text-xl text-gray-500">{{ __('Keep your customers happy even during an outage. Decreasing support requests, increasing customer confidence!') }}</p>
                                </div>
                                <form action="#" class="mt-12 sm:max-w-lg sm:w-full flex justify-center items-center">
                                    <div class="min-w-0 flex-1">
                                        <label for="hero-email" class="sr-only">Stauspage Demo</label>
                                        <a target="_blank" href="https://demo.livck.com" class="mt-1 relative rounded-md shadow-sm">
                                            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-primary">
                                                https://
                                            </div>
<!--                                            <input id="hero-email" type="email" name="email" class="block w-full focus:ring-indigo-500 focus:border-indigo-500 w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="you@example.com" />-->
                                            <input id="hero-email" value="demo.livck.com" type="email" class="pl-[3.9em] pr-16 block w-full border border-gray-300 rounded-md px-5 py-3 text-base text-gray-500 placeholder-gray-500 shadow-sm focus:border-primary-500 focus:ring-primary-500 cursor-pointer" readonly/>
                                            <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                                <button class="block w-full rounded-md border border-transparent bg-primary text-base font-medium text-white shadow hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:px-10">{{ __('See Demo') }}</button>
                                            </div>
                                        </a>
                                    </div>
                                </form>
                                <div class="mt-6">
                                    <div class="inline-flex items-center divide-x divide-gray-300">
                                        <div class="flex-shrink-0 flex pr-5">
                                            <StarIcon class="h-5 w-5 text-yellow-400" aria-hidden="true" />
                                            <StarIcon class="h-5 w-5 text-yellow-400" aria-hidden="true" />
                                            <StarIcon class="h-5 w-5 text-yellow-400" aria-hidden="true" />
                                            <StarIcon class="h-5 w-5 text-yellow-400" aria-hidden="true" />
                                            <StarIcon class="h-5 w-5 text-yellow-400" aria-hidden="true" />
                                        </div>
                                        <div class="min-w-0 flex-1 pl-5 py-1 text-sm text-gray-500 sm:py-3"><span class="font-medium text-gray-900">{{ __('What our customers say') }}</span> - <a href="https://www.trustpilot.com/review/livck.com" target="_blank" class="font-medium text-primary hover:text-primary-600">Trustpilot</a></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="sm:mx-auto sm:max-w-3xl sm:px-6">
                        <div class="py-12 sm:relative sm:mt-12 sm:py-16 lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
                            <div class="hidden sm:block">
                                <svg class="absolute top-8 right-1/2 -mr-3 lg:m-0 lg:left-0" width="404" height="392" fill="none" viewBox="0 0 404 392">
                                    <defs>
                                        <pattern id="837c3e70-6c3a-44e6-8854-cc48c737b659" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                                            <rect x="0" y="0" width="4" height="4" class="text-gray-200" fill="currentColor" />
                                        </pattern>
                                    </defs>
                                    <rect width="404" height="392" fill="url(#837c3e70-6c3a-44e6-8854-cc48c737b659)" />
                                </svg>
                            </div>
                            <div
                                v-motion
                                :initial="{ x: 1000 }"
                                :enter="{ x: 0 }"
                                :delay="400"
                                :duration="1600"
                                class="relative pl-4 -mr-40 sm:mx-auto sm:max-w-3xl sm:px-0 lg:max-w-none lg:h-full lg:pl-12"
                            >
                                <img class="w-full rounded-md shadow-xl ring-1 ring-black ring-opacity-5 lg:h-full lg:w-auto lg:max-w-none" :src="asset('build/statuspage-dashboard.png')" alt="" draggable="false"/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Feature Section -->
            <div class="bg-gray-50 overflow-hidden">
                <div class="relative max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
                    <svg class="absolute top-0 left-full transform -translate-x-1/2 -translate-y-3/4 lg:left-auto lg:right-full lg:translate-x-2/3 lg:translate-y-1/4" width="404" height="784" fill="none" viewBox="0 0 404 784" aria-hidden="true">
                        <defs>
                            <pattern id="8b1b5f72-e944-4457-af67-0c6d15a99f38" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                                <rect x="0" y="0" width="4" height="4" class="text-gray-200" fill="currentColor" />
                            </pattern>
                        </defs>
                        <rect width="404" height="784" fill="url(#8b1b5f72-e944-4457-af67-0c6d15a99f38)" />
                    </svg>

                    <div class="sm:align-center sm:flex sm:flex-col mb-5">
                        <h1 class="text-5xl font-bold tracking-tight text-gray-900 sm:text-center">{{ __('Pricing Plans') }}</h1>
                        <p class="mt-5 text-xl text-gray-500 sm:text-center">{{ __('Designed for small and large infrastructures.') }}</p>
                        <div class="relative mt-6 flex self-center rounded-lg bg-gray-100 p-0.5 sm:mt-8">
                            <button @click="billing_cycle = '30 days'" type="button" class="relative w-1/2 whitespace-nowrap rounded-md py-2 text-sm font-medium shadow-sm focus:z-10 focus:outline-none focus:ring-0 sm:w-auto sm:px-8" :class="{'border-gray-200 text-gray-900 bg-white': billing_cycle === '30 days', 'border-transparent text-gray-700': billing_cycle === '365 days'}">{{ __('Monthly billing') }}</button>
                            <button @click="billing_cycle = '365 days'" type="button" class="relative ml-0.5 w-1/2 whitespace-nowrap rounded-md border py-2 text-sm font-medium focus:z-10 focus:outline-none focus:ring-0 sm:w-auto sm:px-8" :class="{'border-gray-200 text-gray-900 bg-white': billing_cycle === '365 days', 'border-transparent text-gray-700': billing_cycle === '30 days'}">{{ __('Yearly billing') }}</button>
                        </div>
                    </div>

                    <div class="relative">
                        <div class="mx-auto max-w-7xl bg-white shadow-md rounded-md py-16 sm:py-24 sm:px-6 lg:px-8">

                            <!-- xs to lg -->
                            <div class="mx-auto max-w-2xl space-y-16 lg:hidden">
                                <section v-for="item in packageList">
                                    <div class="mb-8 px-4">
                                        <h2 class="text-lg font-medium leading-6 text-gray-900">{{ item.name }}</h2>
                                        <p class="mt-4">
                                            <span class="text-4xl font-bold tracking-tight text-gray-900">{{ item.price }}</span>
                                        </p>
                                        <p class="mt-4 text-sm text-gray-500" v-text="descriptions[item.name.toLowerCase()]"/>
                                        <inertia-link :href="route('order')" class="mt-6 block w-full rounded-md border border-gray-800 bg-gray-800 py-2 text-center text-sm font-semibold text-white hover:bg-gray-900">{{ __('Buy :plan', {plan: item.name}) }}</inertia-link>
                                    </div>

                                    <table class="w-full">
                                        <caption class="border-t border-gray-200 bg-gray-50 py-3 px-4 text-left text-sm font-medium text-gray-900">
                                          {{ __('Monitoring') }}
                                        </caption>
                                        <thead>
                                        <tr>
                                            <th class="sr-only" scope="col">Feature</th>
                                            <th class="sr-only" scope="col">Included</th>
                                        </tr>
                                        </thead>
                                        <tbody class="divide-y divide-gray-200">

                                        <tr v-for="checkType in pricingFeatures.checking" class="border-t border-gray-200">
                                            <th class="py-5 px-4 text-left text-sm font-normal text-gray-500" scope="row">{{ __(checkType) }}</th>
                                            <td class="py-5 pr-4">
                                                <!-- Heroicon name: mini/check -->
                                                <svg class="ml-auto h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                    <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                </svg>
                                                <span class="sr-only">{{ __('Yes') }}</span>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>

                                    <table class="w-full">
                                        <caption class="border-t border-gray-200 bg-gray-50 py-3 px-4 text-left text-sm font-medium text-gray-900">
                                            {{ __('Limitations') }}
                                        </caption>
                                        <thead>
                                        <tr>
                                            <th class="sr-only" scope="col">Feature</th>
                                            <th class="sr-only" scope="col">Included</th>
                                        </tr>
                                        </thead>
                                        <tbody class="divide-y divide-gray-200">

                                        <tr v-for="(o,k) in {'Monitors': 'monitor_limit', 'Categories': 'monitorcategory_limit', 'Announcements': 'alert_limit', 'Newsletter Subscribers': 'newsletter_limit', 'Team Members': 'user_limit'}" class="border-t border-gray-200">
                                            <th class="py-5 px-4 text-left text-sm font-normal text-gray-500" scope="row">{{ __(k) }}</th>
                                            <td class="py-5 pr-4 flex justify-end">
                                                <span class="block text-sm text-gray-700" v-if="item.payload[o] !== -1">{{ __(`Up to :count ${k.toLowerCase()}`, {count: item.payload[o]}) }}</span>
                                                <span class="block text-sm text-gray-700" v-else>{{ __('Unlimited') }}</span>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>

                                    <table class="w-full">
                                        <caption class="border-t border-gray-200 bg-gray-50 py-3 px-4 text-left text-sm font-medium text-gray-900">
                                            {{ __('Alerting') }}
                                        </caption>
                                        <thead>
                                        <tr>
                                            <th class="sr-only" scope="col">Feature</th>
                                            <th class="sr-only" scope="col">Included</th>
                                        </tr>
                                        </thead>
                                        <tbody class="divide-y divide-gray-200">

                                        <tr v-for="alertType in pricingFeatures.alerting" class="border-t border-gray-200">
                                            <th class="py-5 px-4 text-left text-sm font-normal text-gray-500" scope="row">{{ __(alertType) }}</th>
                                            <td class="py-5 pr-4">
                                                <!-- Heroicon name: mini/check -->
                                                <svg class="ml-auto h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                    <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                </svg>
                                                <span class="sr-only">{{ __('Yes') }}</span>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>

<!--                                    <div class="border-t border-gray-200 px-4 pt-5">-->
<!--                                        <a href="#" class="block w-full rounded-md border border-gray-800 bg-gray-800 py-2 text-center text-sm font-semibold text-white hover:bg-gray-900">Buy Basic</a>-->
<!--                                    </div>-->
                                </section>
                            </div>

                            <!-- lg+ -->
                            <div class="hidden lg:block">
                                <table class="h-px w-full table-fixed">
                                    <thead>
                                    <tr>
                                        <th class="px-6 pb-4 text-left text-sm font-medium text-gray-900" scope="col">
                                            <span class="sr-only">Feature by</span>
                                            <span>{{ __('Plans') }}</span>
                                        </th>

                                        <th v-for="item in packageList" :class="packageClassWidth" class="px-6 pb-4 text-left text-lg font-medium leading-6 text-gray-900" scope="col">{{ __(item.name) }}</th>

                                    </tr>
                                    </thead>
                                    <tbody class="divide-y divide-gray-200 border-t border-gray-200">
                                    <tr>
                                        <th class="py-8 px-6 text-left align-top text-sm font-medium text-gray-900" scope="row"></th>
                                        <td v-for="item in packageList" :class="packageClassWidth" class="h-full py-8 px-6 align-top">
                                            <div class="relative table h-full">
                                                <p>
                                                    <span class="text-4xl font-bold tracking-tight text-gray-900">{{ item.price }} €</span>
                                                </p>
                                                <p class="mt-4 mb-16 text-sm text-gray-500" v-text="descriptions[item.name.toLowerCase()]"/>
                                                <inertia-link :href="route('order')" class="5 absolute bottom-0 block w-full flex-grow rounded-md border border-gray-800 bg-gray-800 py-2 text-center text-sm font-semibold text-white hover:bg-gray-900">{{ __('Buy :plan', {plan: item.name}) }}</inertia-link>
                                            </div>
                                        </td>
                                    </tr>

                                    <tr>
                                        <th class="bg-gray-50 py-3 pl-6 text-left text-sm font-medium text-gray-900" colspan="5" scope="colgroup">{{ __('Monitoring') }}</th>
                                    </tr>

                                    <tr v-for="checkType in pricingFeatures.checking">
                                        <th class="py-5 px-6 text-left text-sm font-normal text-gray-500" scope="row">{{ __(checkType) }}</th>

                                        <td v-for="item in packageList" class="py-5 px-6">
                                            <!-- Heroicon name: mini/check -->
                                            <svg class="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                            </svg>
                                            <span class="sr-only">Included in {{ item.name }}</span>
                                        </td>
                                    </tr>

                                    <tr>
                                        <th class="bg-gray-50 py-3 pl-6 text-left text-sm font-medium text-gray-900" colspan="5" scope="colgroup">{{ __('Limitations') }}</th>
                                    </tr>

                                    <tr v-for="(o,k) in {'Monitors': 'monitor_limit', 'Categories': 'monitorcategory_limit', 'Announcements': 'alert_limit', 'Newsletter Subscribers': 'newsletter_limit', 'Team Members': 'user_limit'}">
                                        <th class="py-5 px-6 text-left text-sm font-normal text-gray-500" scope="row">{{ __(k) }}</th>

                                        <td v-for="item in packageList" class="py-5 px-6">
                                            <span class="block text-sm text-gray-700" v-if="item.payload[o] !== -1">{{ __(`Up to :count ${k.toLowerCase()}`, {count: item.payload[o]}) }}</span>
                                            <span class="block text-sm text-gray-700" v-else>{{ __('Unlimited') }}</span>
                                        </td>

                                    </tr>

                                    <tr>
                                        <th class="bg-gray-50 py-3 pl-6 text-left text-sm font-medium text-gray-900" colspan="5" scope="colgroup">{{ __('Alerting') }}</th>
                                    </tr>

                                    <tr v-for="alertType in pricingFeatures.alerting">
                                        <th class="py-5 px-6 text-left text-sm font-normal text-gray-500" scope="row">{{ __(alertType) }}</th>

                                        <td v-for="item in packageList" class="py-5 px-6">
                                            <!-- Heroicon name: mini/check -->
                                            <svg class="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                            </svg>
                                            <span class="sr-only">Included in {{ item.name }}</span>
                                        </td>
                                    </tr>

                                    <tr>
                                        <th class="bg-gray-50 py-3 pl-6 text-left text-sm font-medium text-gray-900" colspan="5" scope="colgroup">{{ __('Statuspage') }}</th>
                                    </tr>

                                    <tr v-for="applicationFeature in pricingFeatures.application">
                                        <th class="py-5 px-6 text-left text-sm font-normal text-gray-500" scope="row">{{ __(applicationFeature) }}</th>

                                        <td v-for="item in packageList" class="py-5 px-6">
                                            <!-- Heroicon name: mini/check -->
                                            <svg class="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                            </svg>
                                            <span class="sr-only">Included in {{ item.name }}</span>
                                        </td>
                                    </tr>

                                    </tbody>
                                    <tfoot>
                                    <tr class="border-t border-gray-200">
                                        <th class="sr-only" scope="row">{{ __('Choose your plan') }}</th>

                                        <td class="px-6 pt-5" v-for="item in packageList">
                                            <inertia-link :href="route('order')" class="block w-full rounded-md border border-gray-800 bg-gray-800 py-2 text-center text-sm font-semibold text-white hover:bg-gray-900">{{ __('Buy :plan', {plan: item.name}) }}</inertia-link>
                                        </td>

<!--                                        <td class="px-6 pt-5">-->
<!--                                            <a href="#" class="block w-full rounded-md border border-gray-800 bg-gray-800 py-2 text-center text-sm font-semibold text-white hover:bg-gray-900">Buy Essential</a>-->
<!--                                        </td>-->

<!--                                        <td class="px-6 pt-5">-->
<!--                                            <a href="#" class="block w-full rounded-md border border-gray-800 bg-gray-800 py-2 text-center text-sm font-semibold text-white hover:bg-gray-900">Buy Premium</a>-->
<!--                                        </td>-->
                                    </tr>
                                    </tfoot>
                                </table>

                            </div>
                        </div>
                    </div>
                </div>
            </div>

          <hr class="divider"/>

            <!-- Extended Section -->
            <cheap-package-preview-box/>

            <!-- Testimonials -->
            <testimonials/>

          <hr class="divider"/>

            <!-- Features -->
            <feature-preview-box/>

          <hr class="divider"/>

            <!-- CTA -->
            <c-t-a-r2-dive-in/>

        </main>
    </guest-layout>
</template>

<script>
import { ChevronRightIcon, StarIcon, CheckCircleIcon } from '@heroicons/vue/solid'
import { GlobeAltIcon, LightningBoltIcon, MailIcon, ScaleIcon } from '@heroicons/vue/outline'
import { Carousel, Slide } from 'vue3-carousel';
import GuestLayout from "@/Layouts/GuestLayout";
import VueWriter from 'vue-writer';
import 'vue3-carousel/dist/carousel.css';
import Checkbox from "@/Jetstream/Checkbox";
import RegisterForm from "@/Pages/Auth/RegisterForm";
import Button from "@/Jetstream/Button";
import FeaturePreviewBox from "@/Pages/Sites/FeatureSections/FeaturePreviewBox";
import CheapPackagePreviewBox from "@/Pages/Sites/FeatureSections/CheapPackagePreviewBox";
import Testimonials from "@/Pages/Sites/FeatureSections/Testimonials";
import CTAR2DiveIn from "@/Pages/Sites/FeatureSections/CTAR2DiveIn";

export default {
    components: {
      CTAR2DiveIn,
      Testimonials,
      CheapPackagePreviewBox,
      FeaturePreviewBox,
      Button,
      RegisterForm,
      Checkbox,
      GuestLayout,
      ChevronRightIcon,
      VueWriter,
      StarIcon,
      GlobeAltIcon,
      LightningBoltIcon,
      MailIcon,
      ScaleIcon,
      CheckCircleIcon,
      Carousel,
      Slide,
    },
    props: {
        packages: Array,
    },
    data() {
        return {
            billing_cycle: '30 days',
            descriptions: {
              smart: 'The perfect plan to get started with your statuspage and to monitor your first services.', // __('The perfect plan to get started with your statuspage and to monitor your first services.')
              starter: 'The perfect plan for small projects and startups.', // __('The perfect plan for small projects and startups.')
              business: 'The perfect plan for small and medium-sized businesses.', // __('The perfect plan for small and medium-sized businesses.')
              extended: 'The perfect plan for large companies and enterprises.', // __('The perfect plan for large companies and enterprises.')
            },
            pricingFeatures: {
                checking: [
                    'TCP', // __('TCP')
                    'HTTP', // __('HTTP')
                    'Manual', // __('Manual')
                    'SSL-Expiration', // __('SSL-Expiration')
                    'Multi-Step incident verification' // __('Multi-Step incident verification')
                ],
                alerting: [
                    'E-Mail',
                    'Slack',
                    'Pushover',
                    'Telegram'
                ],
                application: [
                    'Role Based Access Control', // __('Role Based Access Control')
                    'Two-Factor Authentication', // __('Two-Factor Authentication')
                    'GDPR-compliant', // __('GDPR-compliant')
                    'Password-protected statuspage', // __('Password-protected statuspage')
                    'Custom JS/CSS', // __('Custom JS/CSS')
                    'API', // __('API')
                    'Newsletter', // __('Newsletter')
                    'Discord channel-statuspage integration', // __('Discord channel-statuspage integration')
                    'Multiple themes' // __('Multiple themes')
                ],
            },
        };
    },
    computed: {
        packageClassWidth() {
            return `w-1/${this.packageList.length+1}`;
        },
        packageList() {
            return this.packages.filter(o => o.billing_cycle === this.billing_cycle).sort((a,b) => a.price - b.price);
        }
    },
}
</script>
